import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Tooltip,
} from '@mui/material'
import { EventNote, Search, Warning, ErrorOutline } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'
import { formatNumber, formatPercent } from '../../utils/format'

const MONTHS = [
  'Apr 26','May 26','Jun 26','Jul 26','Aug 26','Sep 26',
  'Oct 26','Nov 26','Dec 26','Jan 27','Feb 27','Mar 27',
]

function genCell(sched, avail) {
  return { scheduled: sched, available: avail, util: avail > 0 ? (sched / avail) * 100 : 0 }
}

const LINES = ['Line 1','Line 2','Line 3','Line 4']

const DEMO_GRID = {
  'Line 1': [genCell(340,352),genCell(330,336),genCell(370,352),genCell(320,352),genCell(310,336),genCell(300,352),genCell(350,352),genCell(280,320),genCell(260,336),genCell(320,352),genCell(310,320),genCell(340,352)],
  'Line 2': [genCell(310,336),genCell(320,336),genCell(350,336),genCell(340,352),genCell(300,336),genCell(290,336),genCell(310,336),genCell(270,320),genCell(250,336),genCell(300,336),genCell(290,320),genCell(320,336)],
  'Line 3': [genCell(280,320),genCell(290,336),genCell(310,320),genCell(270,320),genCell(260,320),genCell(250,320),genCell(280,320),genCell(240,288),genCell(230,320),genCell(260,320),genCell(250,288),genCell(270,320)],
  'Line 4': [genCell(240,288),genCell(250,288),genCell(280,288),genCell(230,288),genCell(220,288),genCell(210,288),genCell(240,288),genCell(200,256),genCell(190,288),genCell(220,288),genCell(210,256),genCell(230,288)],
}

function utilColor(pct) {
  if (pct > 100) return 'error.main'
  if (pct >= 85) return 'warning.main'
  return 'success.main'
}

function utilBg(pct) {
  if (pct > 100) return 'error.light'
  if (pct >= 85) return 'warning.light'
  return 'success.light'
}

export default function Capacity() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const filteredLines = useMemo(
    () => LINES.filter((l) => l.toLowerCase().includes(search.toLowerCase())),
    [search],
  )

  const overloaded = Object.values(DEMO_GRID).flat().filter((c) => c.util > 100).length
  const caution = Object.values(DEMO_GRID).flat().filter((c) => c.util >= 85 && c.util <= 100).length

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <EventNote color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Capacity</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="Capacity Calendar" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v5 · {LINES.length} lines × 12 months
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {overloaded > 0 && (
          <Chip icon={<ErrorOutline />} label={`${overloaded} overloaded`} color="error" size="small" />
        )}
        {caution > 0 && (
          <Chip icon={<Warning />} label={`${caution} caution`} color="warning" size="small" />
        )}
        {overloaded === 0 && caution === 0 && (
          <Chip label="All clear" color="success" size="small" />
        )}
      </Box>

      <TextField
        size="small"
        placeholder="Search lines…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 280 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>Line</TableCell>
              {MONTHS.map((m) => (
                <TableCell key={m} align="center" sx={{ fontWeight: 700, minWidth: 90 }}>{m}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLines.map((line) => (
              <TableRow key={line} hover>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>{line}</TableCell>
                {DEMO_GRID[line].map((cell, i) => (
                  <Tooltip
                    key={i}
                    title={`Scheduled: ${formatNumber(cell.scheduled)}h / Available: ${formatNumber(cell.available)}h`}
                    arrow
                  >
                    <TableCell
                      align="center"
                      onClick={() => navigate(`/plans/current/grid?line=${encodeURIComponent(line)}&month=${MONTHS[i]}`)}
                      sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: utilColor(cell.util) }}>
                          {formatPercent(cell.util, 0)}
                        </Typography>
                        <Box sx={{ width: '100%', height: 4, bgcolor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                          <Box sx={{
                            width: `${Math.min(cell.util, 120)}%`,
                            height: '100%',
                            bgcolor: utilBg(cell.util),
                            borderRadius: 2,
                            maxWidth: '100%',
                          }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          {formatNumber(cell.scheduled)}/{formatNumber(cell.available)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
